/**
 * Copyright (c) 2024 The Diffusion Studio Authors
 *
 * This Source Code Form is subject to the terms of the Mozilla 
 * Public License, v. 2.0 that can be found in the LICENSE file.
 */

import type { MediaClipProps } from '../media';
import { MediaSourceRef } from '../../sources/media-source';
import { Input } from 'mediabunny';

/**
 * Crop configuration - normalized values (0-1) relative to source dimensions.
 * x=0, y=0, width=1, height=1 means no crop (full frame visible).
 */
export interface CropConfig {
    /** X position of crop region (0-1, left edge) */
    x: number;
    /** Y position of crop region (0-1, top edge) */
    y: number;
    /** Width of crop region (0-1) */
    width: number;
    /** Height of crop region (0-1) */
    height: number;
}

/**
 * Configuration for a single input source in a ScriptLayerClip.
 * An Input can have audio and/or video - the ScriptLayerClip will probe
 * and use whichever tracks are available.
 */
export interface ScriptLayerInputConfig {
    /**
     * Optional per-source segment placements on the ScriptClip spine.
     * When present, these segments define where (and which range of) the source plays on the ScriptClip timeline.
     * This is the foundation for "empty scenes" (fillable spine time) and future sequential script sources.
     *
     * Backwards compatibility: if omitted, the clip falls back to using `range`, `offsetSec`, and `playbackRate`.
     */
    segments?: ScriptLayerSourceSegment[];

    /** MediaBunny Input - source of audio and/or video */
    input: Input;
    
    /** Asset ID for this source (used for waveform lookup, etc.) */
    assetId?: string;
    
    /** Display name for this source (e.g., participant name) */
    name?: string;
    
    /** Waveform color for this source's audio */
    color?: string;
    
    /** Audio gain for this source (default: 1.0) */
    gain?: number;
    
    /** 
     * Visual transform for video (only used if input has video track).
     * If not provided, video will be centered and fill the canvas.
     */
    visual?: {
        x: number;
        y: number;
        width: number;
        height: number;
        alpha: number;
        /** Optional shape mask ('none' | 'circle' | 'rounded'). */
        maskShape?: 'none' | 'circle' | 'rounded';
        /** Rotation in degrees (90° steps in UI). */
        rotation?: number;
        /** Optional crop - which part of source to show (default: full frame) */
        crop?: CropConfig;
    };
    
    /**
     * Source time range to use (in seconds).
     * If not provided, the full source duration is used.
     * - inSec: start time in source media (default: 0)
     * - outSec: end time in source media (default: source duration)
     */
    range?: {
        inSec: number;
        outSec: number;
    };
    
    /**
     * Timeline offset in seconds - where this source starts on the composition timeline.
     * This is relative to the ScriptLayerClip's start time.
     * Default: 0 (starts at the beginning of the script layer clip)
     */
    offsetSec?: number;
    
    /**
     * Playback rate for this source.
     * Default: 1.0 (normal speed)
     */
    playbackRate?: number;
    
    /**
     * Layer index for z-ordering this visual element relative to other sources.
     * Lower values render behind higher values.
     * Default: array index (0, 1, 2, ...) - meaning later sources render on top.
     */
    layerIndex?: number;
}

/**
 * Placement of a source segment on the ScriptClip spine timeline.
 * - `offsetSec` is clip-local time where this segment starts.
 * - `range` is the source-time window used for this segment.
 * - `playbackRate` applies only for this segment (falls back to config-level playbackRate).
 */
export interface ScriptLayerSourceSegment {
    id: string;
    offsetSec: number;
    range: {
        inSec: number;
        outSec: number;
    };
    playbackRate?: number;
}

// Legacy interface - kept for backwards compatibility
export interface ScriptLayerVideoSource {
    source: MediaSourceRef; // Can have imgUrl property for images
    x: number;
    y: number;
    width: number;
    height: number;
    alpha: number;
    gain?: number; // Optional per-source audio gain if source has audio
}

export interface ScriptLayerClipProps extends MediaClipProps {
    names?: string[]; // Names of source clips (e.g., participant names in a podcast)
    colors?: string[]; // Colors for each audio source (for multi-colored waveform display)
}

// ============ Transition Types ============

export type AudioTransitionType = 'none' | 'microfade' | 'fade' | 'crossfade';
export type VideoTransitionType = 'cut' | 'fade' | 'smart' | 'dissolve' | 'wipe';

export interface TransitionConfig {
    audio?: {
        type: AudioTransitionType;
        durationMs: number;  // 4ms for microfade, 500ms+ for user fades
    };
    video?: {
        type: VideoTransitionType;
        durationMs: number;
        /** Optional easing curve name (used by 'smart'). */
        easing?: string;
        /**
         * Optional crossfade toggle (used by 'smart').
         * When true, outgoing and incoming scene states overlap visually.
         */
        addFades?: boolean;
    };
}

// Microfade duration for speech cuts (click removal + natural smoothing)
// 15-20ms is short enough to be inaudible but long enough to smooth speech jumps
export const MICROFADE_DURATION_MS = 15;

// Default transitions
export const DEFAULT_CUT_TRANSITION: TransitionConfig = {
    audio: { type: 'microfade', durationMs: MICROFADE_DURATION_MS },
    video: { type: 'cut', durationMs: 0 },
};

export const DEFAULT_SPLIT_TRANSITION: TransitionConfig = {
    audio: { type: 'none', durationMs: 0 },
    video: { type: 'cut', durationMs: 0 },
};

// Default transition for scene boundaries.
// For now, scenes should default to a "hard cut" unless the user explicitly configures a transition.
export const DEFAULT_SCENE_TRANSITION: TransitionConfig = {
    audio: { type: 'none', durationMs: 0 },
    video: { type: 'cut', durationMs: 0 },
};

// ============ Cut Analysis Types ============

/**
 * Result of analyzing a cut point for optimal audio quality.
 * Used to determine zero-crossing snapping and fade durations.
 */
export interface CutPointAnalysis {
    // Original time (composition time)
    originalSec: number;
    // Snapped time after zero-crossing analysis (composition time)
    snappedSec: number;
    // How much we moved the cut point (can be negative)
    snapDeltaSec: number;
    // Whether a clean zero crossing was found
    hasZeroCrossing: boolean;
    // RMS energy at this point (for amplitude mismatch detection)
    rms: number;
    // Instantaneous amplitude at the snapped cut point (for peak detection)
    amplitudeAtCut: number;
}

/**
 * Harshness score breakdown for transparent debugging.
 * Total score >= HARSH_THRESHOLD means the cut is harsh.
 */
export interface HarshnessScore {
    gapScore: number;           // 0-30 points based on remaining gap
    zeroCrossingScore: number;  // 0-25 points based on zero crossing quality
    rmsScore: number;           // 0-25 points based on amplitude mismatch
    amplitudeScore: number;     // 0-20 points based on amplitude at cut point
    total: number;              // Sum of all scores
}

// Threshold for determining if a cut is harsh
export const HARSH_SCORE_THRESHOLD = 39;

/**
 * Complete analysis of a cut (both start and end points).
 */
export interface CutAnalysis {
    // Analysis of the cut start (where we stop playing "before" audio)
    startAnalysis: CutPointAnalysis;
    // Analysis of the cut end (where we resume playing "after" audio)
    endAnalysis: CutPointAnalysis;
    // Recommended fade-out duration at cut start (ms)
    fadeOutMs: number;
    // Recommended fade-in duration at cut end (ms)
    fadeInMs: number;
    // Is this cut likely to sound harsh?
    isHarsh: boolean;
    // RMS ratio between end of "before" and start of "after" (ideal = 1.0)
    rmsRatio: number;
    // Detailed harshness score breakdown
    score: HarshnessScore;
}

// ============ Cut and Split Types ============

/**
 * How to handle transcript words for a cut:
 * - 'delete': Remove words from transcript entirely (default)
 * - 'strikethrough': Keep words but show as strikethrough (for "Ignore Media")
 * - 'retake-strikethrough': Keep words as strikethrough with retake-specific styling
 */
export type CutTranscriptMode = 'delete' | 'strikethrough' | 'retake-strikethrough';

export interface ScriptLayerCut {
    id: string;
    startSec: number;
    endSec: number;
    // Optional analysis results (populated when cut is analyzed)
    analysis?: CutAnalysis;
    groupId?: string;  // For grouped cuts (e.g., multi-track ripple delete)
    transition?: TransitionConfig;  // Override default microfade
    transcriptMode?: CutTranscriptMode;  // How to display words in cut region
}

// ============ Transcript Hidden Ranges ============

/**
 * Range of words to hide from transcript display.
 * Does NOT affect playback - just hides words from view.
 */
export interface TranscriptHiddenRange {
    id: string;
    startSec: number;
    endSec: number;
}

// ============ Transcript Word Corrections ============

/**
 * A controlled correction of a single transcript word.
 * This is transcript-only metadata and does NOT affect playback timing.
 *
 * Identification should be stable across chunking:
 * - `sourceIndex` identifies which ScriptLayerClip input/transcript this came from.
 * - `srcStartSec/srcEndSec` identify the original source-time bounds for the word.
 */
export interface TranscriptWordCorrection {
    id: string;
    sourceIndex?: number;
    srcStartSec?: number;
    srcEndSec?: number;
    correctedText: string;
}

// ============ Transcript Paragraph Breaks ============

/**
 * A manual paragraph break within the transcript UI.
 * Does NOT affect playback - it's editor-only structure.
 *
 * The break is anchored to the word immediately BEFORE the break.
 * Identification is stable across chunking:
 * - `sourceIndex` identifies which ScriptLayerClip input/transcript this came from.
 * - `srcStartSec/srcEndSec` identify the original source-time bounds for the anchor word.
 * - `timeSec` is an optional clip-local fallback (typically the anchor word's endSec).
 */
export interface TranscriptParagraphBreak {
    id: string;
    sourceIndex?: number;
    srcStartSec?: number;
    srcEndSec?: number;
    timeSec?: number;
}

// ============ Transcript Markers ============

export interface TranscriptMarkerAnchor {
    sourceIndex?: number;
    srcStartSec?: number;
    srcEndSec?: number;
    timeSec?: number;
}

/**
 * A transcript-only marker (chapter/header) used for navigation.
 * Does NOT affect captions/export and does NOT affect playback.
 *
 * Markers are anchored between words. For stability under edits:
 * - `after` points to the word immediately AFTER the marker boundary (preferred).
 * - `before` points to the word immediately BEFORE the marker boundary (fallback).
 * - `timeSec` is a clip-local fallback (typically the boundary time).
 */
export interface TranscriptMarker {
    id: string;
    title: string;
    /**
     * Optional marker origin, used to distinguish AI-generated chapters from user-created markers.
     * Example: "ai_chapters"
     */
    origin?: string;
    after?: TranscriptMarkerAnchor;
    before?: TranscriptMarkerAnchor;
    timeSec?: number;
    createdAt?: number;
    updatedAt?: number;
}

// ============ Gap Segments ============

/**
 * Silent gap in the timeline.
 * Maintains timing position but outputs silence.
 */
export interface ScriptLayerGap {
    id: string;
    startSec: number;
    endSec: number;
}

/**
 * Inserted silent time on the ScriptClip spine.
 * Unlike ScriptLayerGap (which replaces existing content with silence),
 * a gap insertion ADDS new time to the composition timeline and shifts all
 * downstream content later.
 *
 * `atSec` is in script-clip local time (same domain as transcript words / cuts / splits).
 */
export interface ScriptLayerGapInsertion {
    id: string;
    /** Clip-local time where the insertion occurs */
    atSec: number;
    /** Duration of inserted silence */
    durationSec: number;
}

// ============ Speaker Override ============

/**
 * Manual speaker assignment for a time range.
 * Overrides automatic diarization-based speaker detection.
 * Used when user manually changes speaker for a section of transcript.
 */
export interface SpeakerOverride {
    id: string;
    /** Start time in composition/clip time */
    startSec: number;
    /** End time in composition/clip time */
    endSec: number;
    /**
     * Which transcript source this override applies to.
     * Required to disambiguate diarization speaker IDs across different sources.
     */
    sourceIndex?: number;
    /** Display name for this speaker */
    speakerName: string;
    /** Color for this speaker (optional - will use default if not set) */
    color?: string;
    /** Reference to speaker entity in Firestore (speakerId) */
    speakerId?: string;
    /** Diarization speaker ID from transcript (e.g., "speaker_0", "speaker_1") -
     *  used to match override to specific diarized speaker, not all speakers in time range */
    diarizationSpeakerId?: string;
}

/**
 * Speaker-targeted audio mute for a time range.
 * This affects playback/export audio only (transcript text is unchanged).
 *
 * Ranges are persisted separately from gaps so muting/unmuting a speaker
 * never destroys manual gap edits.
 */
export interface SpeakerMuteRange {
    id: string;
    /** Start time in composition/clip time */
    startSec: number;
    /** End time in composition/clip time */
    endSec: number;
    /** Transcript source index (index into script sources). */
    sourceIndex?: number;
    /** Diarization speaker ID (e.g. "speaker_0") */
    diarizationSpeakerId?: string;
    /** Optional speaker entity ID (Firestore) */
    speakerId?: string;
    /** Optional display name snapshot (for debugging/UI) */
    speakerName?: string;
}

export interface ScriptLayerSplit {
    id: string;
    timeSec: number;
    isSceneBoundary?: boolean;  // Scene break vs regular edit point
    label?: string;             // Optional label like "Scene 2"
    transition?: TransitionConfig;  // User-specified transition
}

// ============ Scene Types ============

/**
 * Per-scene visual properties for video sources.
 * These override the source-level visual settings for this scene.
 */
export interface SceneVisualProperties {
    /** Override opacity for this scene (0-1, where 1 = fully visible) */
    alpha?: number;
    /**
     * When true, this source is explicitly hidden by the user for this scene.
     * Layout application should respect this and avoid auto-revealing the source.
     */
    hiddenByUser?: boolean;
    /** Override X position for this scene */
    x?: number;
    /** Override Y position for this scene */
    y?: number;
    /** Override width for this scene */
    width?: number;
    /** Override height for this scene */
    height?: number;
    /** Optional crop override for this scene (normalized source coords, 0-1). */
    crop?: CropConfig;
    /** Optional shape mask override for this scene ('none' | 'circle' | 'rounded'). */
    maskShape?: 'none' | 'circle' | 'rounded';
}

/**
 * Per-scene properties that override clip-level defaults.
 * Only non-undefined values will override the clip defaults.
 */
export interface SceneProperties {
    /** Override clip gain for this scene (1.0 = normal, 2.0 = double) */
    gain?: number;
    /** Override clip playback rate for this scene (1.0 = normal, 0.5 = half speed) */
    playbackRate?: number;
    /**
     * Per-audio-source gain overrides for this scene.
     * Key: audio source index (index into ScriptLayerClip.getAudioEntries()).
     * Value: gain multiplier for that source in this scene (0 = muted, 1 = default).
     * When not set for a source, the clip's base per-source gain is used.
     */
    audioSourceGains?: Map<number, number>;
    /** Override the canvas background color for this scene (hex string, e.g. "#000000"). */
    backgroundColor?: string;
    /**
     * Per-source visual overrides for this scene.
     * Key: source index (0, 1, 2...), Value: visual properties for that source in this scene.
     */
    visualOverrides?: Map<number, SceneVisualProperties>;
    /**
     * Per-aspect visual overrides for this scene.
     * Keys: 'horizontal' (16:9), 'vertical' (9:16), 'square' (1:1).
     *
     * When present, overrides are looked up by current canvas aspect instead of `visualOverrides`.
     * `visualOverrides` remains as a legacy fallback (primarily for old persisted scenes).
     */
    visualOverridesByAspect?: Record<string, Map<number, SceneVisualProperties>>;
    /**
     * Layout preset name for this scene (legacy single-aspect).
     * Interpreted as the horizontal (16:9) layout when `layoutByAspect` is not set.
     */
    layout?: string;
    /**
     * Per-aspect layout preset.
     * Keys: 'horizontal' (16:9), 'vertical' (9:16), 'square' (1:1).
     * Value:
     * - string = explicit layout preset
     * - null = explicit "no layout" (use default placement)
     * - undefined / missing key = not set (allow heuristic default)
     */
    layoutByAspect?: Record<string, string | null>;
    /**
     * Transition config for when this scene starts (in-transition).
     * Applies at the beginning of this scene (after the previous scene boundary).
     */
    inTransition?: TransitionConfig;
    /**
     * Transition config for when this scene ends (out-transition).
     * Applies at the end of this scene (before the next scene boundary).
     */
    outTransition?: TransitionConfig;
}

// ============ Automatic Multicam ============

export type MulticamCameraAssignmentKind = "speaker" | "shared" | "unassigned";

export interface MulticamCameraAssignment {
    /** Script source index (index into ScriptLayerClip.getVideoEntries()) */
    sourceIndex: number;
    /** How this camera should be treated by automatic multicam */
    kind: MulticamCameraAssignmentKind;
    /** Firestore speaker entity ID when kind === "speaker" */
    speakerId?: string;
}

export interface MulticamCameraSetup {
    version: 1;
    assignments: MulticamCameraAssignment[];
}

/**
 * Represents a scene derived from scene boundary splits.
 * A scene is a span of content between two scene boundaries (or clip start/end).
 */
export interface ScriptLayerScene {
    /** Scene index (0 = first scene) */
    index: number;
    /** Start time in source/clip-local time */
    startSec: number;
    /** End time in source/clip-local time */
    endSec: number;
    /** Start time on timeline (accounting for per-scene playback rates) */
    timelineStartSec: number;
    /** End time on timeline (accounting for per-scene playback rates) */
    timelineEndSec: number;
    /** Optional label for this scene */
    label?: string;
    /** Split ID that marks the START of this scene (undefined for scene 0) */
    startSplitId?: string;
    /** Scene-specific properties (gain, playbackRate) */
    properties: SceneProperties;
}

// ============ Derived Segment Type ============

export interface ScriptLayerSegment {
    srcStartSec: number;
    srcEndSec: number;
    compStartSec: number;
    // Resolved transitions at segment boundaries
    startTransition?: TransitionConfig;
    endTransition?: TransitionConfig;
    // Gap segments output silence instead of source audio
    isGap?: boolean;
}

// ============ Crossfade Descriptor ============

/**
 * Describes a true crossfade between two segments.
 * Used to pre-mix overlapping audio from before/after a cut point.
 */
export interface CrossfadeDescriptor {
    /** Which audio source (index into inputs array) */
    sourceIndex: number;
    
    // Source times for the "before" segment (tail of A)
    /** Where to start reading tail of segment A (srcBeforeEndSec - fadeDuration) */
    srcBeforeStartSec: number;
    /** Where segment A ends (the cut point in source time) */
    srcBeforeEndSec: number;
    
    // Source times for the "after" segment (head of B)
    /** Where segment B begins (after the cut in source time) */
    srcAfterStartSec: number;
    /** Where to stop reading head of B (srcAfterStartSec + fadeDuration) */
    srcAfterEndSec: number;
    
    // Composition time
    /** Where crossfade begins in composition time */
    compCrossfadeStartSec: number;
    /** Where crossfade ends in composition time */
    compCrossfadeEndSec: number;
    
    /** Duration of the crossfade in seconds (derived from audio analysis) */
    fadeDurationSec: number;
    
    /** Index of the segment AFTER the cut (for reference) */
    segmentAfterIndex: number;
}
